import React, { useState, useEffect, useCallback } from 'react';
import './AdminUsers.css';
import './PersonalAnalysis.css';
import './StudentResources.css';
import { apiFetch } from './api';

/* ── Web kaynak site etiketleri ── */
const SITE_LABELS = {
  youtube: 'YouTube', google: 'Google', wikipedia: 'Wikipedia',
  acikders: 'Açık Ders', udemy: 'Udemy', coursera: 'Coursera',
  edx: 'edX', khan: 'Khan Academy', mitocw: 'MIT OCW', web: 'Web',
};

const siteLabel = (key) => SITE_LABELS[key] || (key ? key : 'Web');

/* Kaydedilmiş web kaynağında url yoksa site + başlıktan arama linki üret */
const buildWebUrl = (r) => {
  if (r?.url && /^https?:\/\//i.test(r.url)) return r.url;
  const q = encodeURIComponent(r?.title || r?.description || '');
  switch (r?.site_key) {
    case 'youtube':   return `https://www.youtube.com/results?search_query=${q}`;
    case 'wikipedia': return `https://tr.wikipedia.org/w/index.php?search=${q}`;
    case 'acikders':  return `https://www.acikders.org.tr/course/search.php?search=${q}`;
    case 'udemy':     return `https://www.udemy.com/courses/search/?q=${q}`;
    case 'coursera':  return `https://www.coursera.org/search?query=${q}`;
    case 'edx':       return `https://www.edx.org/search?q=${q}`;
    case 'khan':      return `https://tr.khanacademy.org/search?page_search_query=${q}`;
    case 'mitocw':    return `https://ocw.mit.edu/search/?q=${q}`;
    default:          return `https://www.google.com/search?q=${q}`;
  }
};

const typeIcon = (t) => {
  switch (t) {
    case 'video':  return '▶';
    case 'course': return '🎓';
    case 'pdf':    return '📄';
    default:       return '🔗';
  }
};

/* ── Tek bir ÖÇ kartı: çalışma ipucu + videolar + web kaynakları ── */
function OutcomeResources({ item }) {
  const videos = item?.videos || [];
  const webRes = item?.web_resources || [];
  if (!videos.length && !webRes.length) return null;

  return (
    <div className="sr-outcome">
      <div className="sr-outcome-head">
        <span className="sr-outcome-badge">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
          </svg>
          {item.outcome_code || 'Öğrenme Çıktısı'}
        </span>
        <span className="sr-outcome-count">
          {videos.length > 0 && `${videos.length} video`}
          {videos.length > 0 && webRes.length > 0 && ' · '}
          {webRes.length > 0 && `${webRes.length} kaynak`}
        </span>
      </div>

      {item.study_tip && (
        <div className="sr-tip">
          <span className="sr-tip-icon">💡</span>
          <p className="sr-tip-text">{item.study_tip}</p>
        </div>
      )}

      {videos.length > 0 && (
        <div className="sr-group">
          <span className="sr-group-label">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            Video Önerileri
          </span>
          <div className="sr-video-grid">
            {videos.map((v, i) => {
              const url = v.video_url
                || `https://www.youtube.com/results?search_query=${encodeURIComponent(v.search_query || v.title || '')}`;
              const title = v.title || 'YouTube videosu';
              const dur = v.duration_sec ? `${Math.floor(v.duration_sec / 60)}:${String(v.duration_sec % 60).padStart(2, '0')}`
                : v.duration_min ? `${v.duration_min} dk` : null;
              return (
                <a key={`v-${i}`} href={url} target="_blank" rel="noopener noreferrer"
                   className="sr-video">
                  <div className="sr-video-thumb">
                    {v.thumbnail
                      ? <img src={v.thumbnail} alt="" loading="lazy" />
                      : <div className="sr-video-noimg" />}
                    <span className="sr-video-play">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                    </span>
                    {dur && <span className="sr-video-dur">{dur}</span>}
                  </div>
                  <div className="sr-video-body">
                    <span className="sr-video-title">{title}</span>
                    <span className="sr-video-channel">{v.channel || 'YouTube'}</span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {webRes.length > 0 && (
        <div className="sr-group">
          <span className="sr-group-label">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
            Web Kaynakları
          </span>
          <div className="sr-web-grid">
            {webRes.map((r, i) => {
              const url = buildWebUrl(r);
              const title = r.title || siteLabel(r.site_key);
              return (
                <a key={`w-${i}`} href={url} target="_blank" rel="noopener noreferrer"
                   className="sr-web">
                  <span className="sr-web-icon">{typeIcon(r.type)}</span>
                  <div className="sr-web-body">
                    <span className="sr-web-title">{title}</span>
                    <span className="sr-web-site">{siteLabel(r.site_key)}</span>
                  </div>
                  <svg className="sr-web-arrow" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7" /><path d="M7 7h10v10" /></svg>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SkeletonSection() {
  return (
    <div className="pa-section">
      <div className="pa-section-header">
        <div className="pa-skeleton-block" style={{ width: 160, height: 14 }} />
      </div>
      {[1, 2].map((i) => (
        <div key={i} className="pa-skeleton-row">
          <div className="pa-skeleton-block" style={{ width: '30%', height: 10 }} />
          <div className="pa-skeleton-block" style={{ width: '90%', height: 40 }} />
        </div>
      ))}
    </div>
  );
}

function StudentResources() {
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setCoursesLoading(true);
        const res = await apiFetch('/student/courses');
        const d = await res.json();
        if (d.success && d.courses) setCourses(d.courses);
      } catch {
        setError('Dersler yüklenemedi');
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const fetchResources = useCallback(async (courseId) => {
    try {
      setLoading(true);
      setData(null);
      setError('');
      const res = await apiFetch(`/student/courses/${courseId}/my-resources`);
      const d = await res.json();
      if (d.success) setData(d);
      else setError(d.message || 'Kaynaklar yüklenemedi');
    } catch {
      setError('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    fetchResources(course.course_id);
  };

  const getCourseLabel = (c) => {
    const code = (c.course_code || '').trim().toUpperCase();
    const name = c.course_name || `Ders ${c.course_id}`;
    return code ? `${code} — ${name}` : name;
  };

  const renderDetail = () => {
    if (loading) return (<><SkeletonSection /><SkeletonSection /></>);
    if (!data) return null;

    if (!data.has_data) {
      return (
        <div className="pa-empty" style={{ padding: '2.5rem 2rem' }}>
          <div className="pa-empty-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="pa-empty-text">Bu ders için henüz kaynak önerisi yok</div>
          <div className="pa-empty-sub">
            Akademisyeniniz analizinizi tamamladığında önerilen video ve web kaynakları burada görünür.
          </div>
        </div>
      );
    }

    return (
      <div className="sr-list">
        {(data.analyses || []).map((item, i) => (
          <OutcomeResources key={item.outcome_code || i} item={item} />
        ))}
      </div>
    );
  };

  return (
    <div className="au-page sr-page">
      <div className="au-toolbar">
        <div className="au-toolbar-top">
          <div className="au-title-block">
            <h2 className="au-title">Kaynak Önerileri</h2>
            <p className="au-subtitle">
              Zayıf olduğunuz öğrenme çıktıları için önerilen video ve web kaynakları.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="pa-flash error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {/* Ders seçici — üstte yatay */}
      <div className="sr-course-bar">
        <span className="sr-course-bar-label">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
          Ders
        </span>
        {coursesLoading ? (
          <div className="sr-course-select sr-course-placeholder">Dersler yükleniyor…</div>
        ) : courses.length === 0 ? (
          <div className="sr-course-select sr-course-placeholder">Kayıtlı ders bulunamadı</div>
        ) : (
          <div className="sr-select-wrap">
            <select
              className="sr-course-select"
              value={selectedCourse?.course_id || ''}
              onChange={(e) => {
                const c = courses.find((x) => String(x.course_id) === e.target.value);
                if (c) handleCourseSelect(c);
              }}
            >
              <option value="" disabled>— Ders seçin —</option>
              {courses.map((c) => (
                <option key={c.course_id} value={c.course_id}>{getCourseLabel(c)}</option>
              ))}
            </select>
            <svg className="sr-select-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        )}
      </div>

      {/* Kaynaklar — tek sütun, ortalı */}
      <div className="sr-content">
        {!selectedCourse ? (
          <div className="pa-empty">
            <div className="pa-empty-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="23 7 16 12 23 17 23 7" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
            </div>
            <div className="pa-empty-text">Bir ders seçin</div>
            <div className="pa-empty-sub">
              Yukarıdan ders seçerek size özel video ve web kaynağı önerilerini görüntüleyebilirsiniz.
            </div>
          </div>
        ) : (
          <>
            <div className="pa-detail-header">
              <span className="pa-detail-eyebrow">Önerilen Kaynaklar</span>
              <h2 className="pa-detail-title">{getCourseLabel(selectedCourse)}</h2>
              {data?.updated_at && (
                <span className="sr-updated">
                  Son güncelleme: {new Date(data.updated_at).toLocaleDateString('tr-TR')}
                </span>
              )}
            </div>
            {renderDetail()}
          </>
        )}
      </div>
    </div>
  );
}

export default StudentResources;
